import { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { useAuthStore } from "@/store/auth";
import { apiPost, API_BASE } from "@/lib/api";
import { AuthResponseSchema, OnboardRequestSchema } from "@ekorank/shared";
import type { AIModel, CheckInterval } from "@ekorank/shared";
import { AI_MODELS, ENABLED_MODELS, MODEL_CREDIT_COST, MODEL_LABELS, PROMPT_MAX_LENGTH } from "@ekorank/shared";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { z } from "zod";

function EyeIcon({ open }: { open: boolean }) {
  if (open) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    );
  }
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

function isValidEmail(email: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

const INDUSTRIES = [
  "Marketing",
  "SaaS",
  "E-commerce",
  "Legal",
  "Healthcare",
  "Finance",
  "Education",
  "Real Estate",
  "Consulting",
  "Other",
];

const STEPS = ["Account", "Brand", "Prompts", "Models & Alerts"] as const;

type PreviewData = {
  domain: string
  mentioned: boolean
  score: number
  sentiment: "positive" | "neutral" | "negative" | null
  summary: string
  key_topics: string[]
  suggested_prompt?: string
}

// Wrapped auth response shape from API
const ApiAuthResponse = z.object({
  ok: z.literal(true),
  data: AuthResponseSchema,
});
const ApiOnboardResponse = z.object({
  ok: z.literal(true),
  data: z.object({
    brandId: z.string(),
    jobId: z.string(),
    message: z.string(),
  }),
});

export default function Signup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setAuth = useAuthStore((s) => s.setAuth);
  const existingUser = useAuthStore((s) => s.user);

  // Skip registration if already authenticated or arriving from OAuth callback
  const isOAuthOnboarding = searchParams.get("step") === "1"
  const initialStep = existingUser || isOAuthOnboarding ? 1 : 0;
  const [step, setStep] = useState(initialStep);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 0 — Account
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef<TurnstileInstance>(null);

  // Step 1 — Brand
  const [brandName, setBrandName] = useState("");
  const [brandUrl, setBrandUrl] = useState("");
  const [industry, setIndustry] = useState("");

  // Step 2 — Prompts
  const [prompts, setPrompts] = useState<string[]>([]);
  const [promptInput, setPromptInput] = useState("");

  // Step 3 — Models & Alerts
  const [selectedModels, setSelectedModels] = useState<AIModel[]>([
    "claude-haiku",
  ]);
  const [alertType, setAlertType] = useState<"email" | "webhook" | "">("");
  const [alertDestination, setAlertDestination] = useState("");

  // Preview teaser (from ?preview=<token> on the URL)
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  useEffect(() => {
    const token = searchParams.get("preview");
    if (!token) return;
    fetch(`${API_BASE}/api/preview-analysis/${encodeURIComponent(token)}`)
      .then((r) => r.json())
      .then((d: unknown) => {
        if (!(d && typeof d === "object" && "ok" in d && (d as { ok: boolean }).ok)) return;
        const data = (d as { ok: boolean; data: PreviewData }).data;
        setPreviewData(data);
        // Pre-fill brand fields from the preview
        setBrandUrl(`https://${data.domain}`);
        const name = data.domain.replace(/^www\./, "").split(".")[0] ?? "";
        setBrandName(name.charAt(0).toUpperCase() + name.slice(1));
        if (data.suggested_prompt) setPrompts([data.suggested_prompt]);
      })
      .catch(() => {});
  }, [searchParams]);

  // Track whether the user arrived at this page unauthenticated (i.e., they came to register).
  // Used to prevent the early-return below from kicking them to dashboard right after they register.
  const wasUnauthed = useRef(!existingUser && !isOAuthOnboarding);
  const hasPreviewToken = !!searchParams.get("preview");

  // All hooks done — safe to early-return now.
  // Allow through if: OAuth new-user onboard, has a preview token to claim, or just registered.
  if (existingUser && !isOAuthOnboarding && !hasPreviewToken && !wasUnauthed.current) {
    return <Navigate to="/dashboard" replace />;
  }

  const addPrompt = () => {
    const p = promptInput.trim();
    if (p.length >= 10 && !prompts.includes(p)) {
      setPrompts((prev) => [...prev, p]);
      setPromptInput("");
    }
  };

  const toggleModel = (model: AIModel) => {
    setSelectedModels((prev) =>
      prev.includes(model) ? prev.filter((m) => m !== model) : [...prev, model],
    );
  };

  const handleRegister = async () => {
    setLoading(true);
    setError(null);
    const result = await apiPost("/api/auth/register", ApiAuthResponse, {
      email,
      password,
      turnstileToken,
    });
    setLoading(false);
    if (!result.ok) {
      setError(result.error);
      setTurnstileToken("");
      turnstileRef.current?.reset();
      return;
    }
    setAuth(result.data.data.user, result.data.data.token);
    setStep(1);
  };

  const handleOnboard = async () => {
    if (selectedModels.length === 0) {
      setError("Select at least one AI model");
      return;
    }
    if (prompts.length === 0) {
      setError("Add at least one prompt");
      return;
    }

    setLoading(true);
    setError(null);

    const payload = {
      brandName,
      brandUrl: brandUrl || undefined,
      industry,
      prompts,
      models: selectedModels,
      checkInterval: "weekly" as CheckInterval,
      alertType: alertType || undefined,
      alertDestination: alertDestination || undefined,
    };

    const result = await apiPost("/api/onboard", ApiOnboardResponse, payload);
    setLoading(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    navigate("/dashboard");
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4 py-12" style={{ backgroundImage: 'radial-gradient(circle, rgb(255 255 255 / 0.035) 1px, transparent 1px)', backgroundSize: '24px 24px' }}>
      {/* Logo */}
      <div className="flex items-center gap-2 mb-10">
        <span className="text-primary text-2xl font-bold">▐</span>
        <span className="text-xl font-bold tracking-tighter text-white">
          Ekorank
        </span>
      </div>

      {/* Progress bar */}
      <div className="w-full max-w-lg mb-8">
        <div className="flex items-center justify-between mb-2">
          {STEPS.map((label, i) => (
            <div
              key={label}
              className={`text-[10px] font-mono uppercase tracking-widest ${
                i <= step ? "text-primary" : "text-outline"
              }`}
            >
              {label}
            </div>
          ))}
        </div>
        <div className="h-1 bg-surface-variant rounded-full overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-500 ease-out"
            style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Card */}
      <div className="relative w-full max-w-lg bg-surface-container border border-outline-variant/20 rounded-xl p-8 shadow-2xl">
        {step >= 1 && (
          <button
            onClick={() => navigate("/dashboard")}
            className="absolute top-4 right-4 text-outline hover:text-on-surface transition-colors"
            aria-label="Go to dashboard"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
        {error && (
          <div className="mb-6 p-3 rounded-lg bg-error/10 border border-error/30 text-error text-sm">
            {error}
          </div>
        )}

        {/* Step 0 — Account */}
        {step === 0 &&
          (() => {
            const emailError =
              emailTouched && email && !isValidEmail(email)
                ? "Invalid email address"
                : null;
            const passwordError =
              passwordTouched && password && password.length < 8
                ? "Minimum 8 characters"
                : null;
            const canSubmit =
              !loading &&
              isValidEmail(email) &&
              password.length >= 8 &&
              !!turnstileToken;
            return (
              <div className="space-y-5 animate-in slide-in-from-right">
                {previewData && (
                  <div className="p-4 rounded-xl bg-primary/5 border border-primary/20 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-mono uppercase tracking-widest text-primary">AI Visibility Preview</span>
                      <span className="text-[10px] font-mono text-outline">{previewData.domain}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-2xl font-bold text-primary">{previewData.score}<span className="text-sm text-outline font-normal">/100</span></span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-mono ${previewData.mentioned ? "bg-secondary/10 text-secondary" : "bg-error/10 text-error"}`}>
                        {previewData.mentioned ? "Mentioned by AI" : "Not mentioned"}
                      </span>
                    </div>
                    <p className="text-xs text-on-surface-variant leading-relaxed">{previewData.summary}</p>
                    <p className="text-[10px] text-outline">Sign up to run a full multi-model analysis →</p>
                  </div>
                )}
                <div>
                  <h2 className="text-2xl font-bold text-white">
                    Create your account
                  </h2>
                  <p className="mt-1 text-sm text-on-surface-variant">
                    {previewData ? "Sign up to unlock your full AI visibility report" : "Start tracking your AI visibility"}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <a
                    href={`${API_BASE}/api/auth/github`}
                    className="flex items-center justify-center gap-2 py-2.5 rounded-lg border border-outline-variant/30 bg-surface-container-low text-white text-sm hover:bg-surface-variant transition-colors"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"/></svg>
                    GitHub
                  </a>
                  <a
                    href={`${API_BASE}/api/auth/google`}
                    className="flex items-center justify-center gap-2 py-2.5 rounded-lg border border-outline-variant/30 bg-surface-container-low text-white text-sm hover:bg-surface-variant transition-colors"
                  >
                    <svg viewBox="0 0 24 24" width="18" height="18"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                    Google
                  </a>
                </div>
                <div className="relative flex items-center gap-3">
                  <div className="flex-1 h-px bg-outline-variant/20" />
                  <span className="text-[11px] text-outline uppercase tracking-widest">or</span>
                  <div className="flex-1 h-px bg-outline-variant/20" />
                </div>
                <div>
                  <label className="block font-mono text-[10px] text-outline uppercase tracking-widest mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={() => setEmailTouched(true)}
                    className={`w-full bg-surface-container-low border-b text-white p-3 outline-none transition-colors placeholder:text-outline/50 ${
                      emailError
                        ? "border-error"
                        : "border-outline focus:border-secondary"
                    }`}
                    placeholder="you@company.com"
                    autoComplete="email"
                  />
                  {emailError && (
                    <p className="mt-1.5 text-xs text-error">{emailError}</p>
                  )}
                </div>
                <div>
                  <label className="block font-mono text-[10px] text-outline uppercase tracking-widest mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onBlur={() => setPasswordTouched(true)}
                      className={`w-full bg-surface-container-low border-b text-white p-3 pr-11 outline-none transition-colors placeholder:text-outline/50 ${
                        passwordError
                          ? "border-error"
                          : "border-outline focus:border-secondary"
                      }`}
                      placeholder="Minimum 8 characters"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-outline hover:text-on-surface transition-colors"
                      tabIndex={-1}
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                    >
                      <EyeIcon open={showPassword} />
                    </button>
                  </div>
                  {passwordError && (
                    <p className="mt-1.5 text-xs text-error">{passwordError}</p>
                  )}
                </div>
                <div className="flex justify-center">
                  <Turnstile
                    ref={turnstileRef}
                    siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY as string}
                    onSuccess={setTurnstileToken}
                    onError={() => setTurnstileToken("")}
                    onExpire={() => setTurnstileToken("")}
                    options={{ theme: "dark" }}
                  />
                </div>
                <button
                  onClick={handleRegister}
                  disabled={!canSubmit}
                  className="w-full py-3.5 rounded-lg bg-gradient-to-r from-primary to-secondary text-on-primary-container font-bold disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-opacity"
                >
                  {loading ? <LoadingSpinner size="sm" /> : "Continue"}
                </button>
                <p className="text-center text-xs text-outline">
                  Already have an account?{" "}
                  <a href="/login" className="text-primary hover:underline">
                    Sign in
                  </a>
                </p>
              </div>
            );
          })()}

        {/* Step 1 — Brand */}
        {step === 1 && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-white">
              Tell us about your brand
            </h2>
            <div>
              <label className="block font-mono text-[10px] text-outline uppercase tracking-widest mb-2">
                Brand Name
              </label>
              <input
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                className="w-full bg-surface-container-low border-b border-outline focus:border-secondary text-white p-3 outline-none transition-colors"
                placeholder="Acme Inc."
              />
            </div>
            <div>
              <label className="block font-mono text-[10px] text-outline uppercase tracking-widest mb-2">
                Website URL (optional)
              </label>
              <input
                value={brandUrl}
                onChange={(e) => setBrandUrl(e.target.value)}
                className="w-full bg-surface-container-low border-b border-outline focus:border-secondary text-white p-3 outline-none transition-colors"
                placeholder="https://acme.com"
              />
            </div>
            <div>
              <label className="block font-mono text-[10px] text-outline uppercase tracking-widest mb-2">
                Industry
              </label>
              <select
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                className="w-full bg-surface-container-low border-b border-outline focus:border-secondary text-white p-3 outline-none transition-colors"
              >
                <option value="">Select industry...</option>
                {INDUSTRIES.map((ind) => (
                  <option key={ind} value={ind}>
                    {ind}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex gap-3">
              {initialStep === 0 && (
                <button
                  onClick={() => setStep(0)}
                  className="flex-1 py-3 rounded-lg border border-outline-variant/30 text-white hover:bg-white/5 transition-colors"
                >
                  Back
                </button>
              )}
              <button
                onClick={() => setStep(2)}
                disabled={!brandName || !industry}
                className="flex-1 py-3 rounded-lg bg-gradient-to-r from-primary to-secondary text-on-primary-container font-bold disabled:opacity-50"
              >
                Continue
              </button>
            </div>
            <p className="text-center">
              <button
                onClick={() => navigate("/dashboard")}
                className="text-xs text-outline hover:text-on-surface-variant hover:underline transition-colors"
              >
                Skip for now →
              </button>
            </p>
          </div>
        )}

        {/* Step 2 — Prompts */}
        {step === 2 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-white">Prompts to track</h2>
              <p className="mt-1 text-sm text-on-surface-variant">
                Natural language questions you want AI models to answer (e.g. "What's the best tax firm in Idaho?")
              </p>
            </div>
            <div className="space-y-2">
              <textarea
                value={promptInput}
                onChange={(e) => setPromptInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    addPrompt();
                  }
                }}
                rows={2}
                maxLength={PROMPT_MAX_LENGTH}
                className="w-full bg-surface-container-low border-b border-outline focus:border-secondary text-white p-3 outline-none transition-colors resize-none"
                placeholder="What's the best tax firm in Idaho?"
              />
              <div className="flex items-center justify-between">
                <span className="text-xs font-mono text-outline">{promptInput.length} / {PROMPT_MAX_LENGTH}</span>
                <button
                  onClick={addPrompt}
                  disabled={promptInput.trim().length < 10}
                  className="px-4 py-2 rounded-lg bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 transition-colors disabled:opacity-40 text-sm"
                >
                  Add
                </button>
              </div>
            </div>
            <div className="space-y-1.5 max-h-36 overflow-y-auto">
              {prompts.map((p, i) => (
                <div key={i} className="flex items-start gap-2 px-3 py-2 rounded-lg bg-surface-container-high border border-outline-variant/20">
                  <span className="text-xs text-on-surface flex-1 leading-snug">{p}</span>
                  <button
                    onClick={() => setPrompts((prev) => prev.filter((_, j) => j !== i))}
                    className="text-outline hover:text-error text-xs shrink-0 mt-0.5"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setStep(1)}
                className="flex-1 py-3 rounded-lg border border-outline-variant/30 text-white hover:bg-white/5 transition-colors"
              >
                Back
              </button>
              <button
                onClick={() => setStep(3)}
                disabled={prompts.length === 0}
                className="flex-1 py-3 rounded-lg bg-gradient-to-r from-primary to-secondary text-on-primary-container font-bold disabled:opacity-50"
              >
                Continue
              </button>
            </div>
            <p className="text-center">
              <button
                onClick={() => navigate("/dashboard")}
                className="text-xs text-outline hover:text-on-surface-variant hover:underline transition-colors"
              >
                Skip for now →
              </button>
            </p>
          </div>
        )}

        {/* Step 3 — Models & Alerts */}
        {step === 3 && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-white">
              AI Models & Alerts
            </h2>

            <div>
              <label className="block font-mono text-[10px] text-outline uppercase tracking-widest mb-4">
                AI Models to query
              </label>
              <div className="space-y-2">
                {AI_MODELS.map((model) => {
                  const enabled = ENABLED_MODELS.has(model);
                  const checked = selectedModels.includes(model);
                  const creditCost = MODEL_CREDIT_COST[model];
                  return (
                    <label
                      key={model}
                      className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border transition-colors ${
                        !enabled
                          ? "border-outline-variant/5 opacity-40 cursor-not-allowed"
                          : checked
                            ? "border-primary/40 bg-primary/5 cursor-pointer"
                            : "border-outline-variant/10 hover:border-outline-variant/20 cursor-pointer"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={!enabled}
                        onChange={() => enabled && toggleModel(model)}
                        className="accent-primary"
                      />
                      <span className="text-sm text-white flex-1">{MODEL_LABELS[model]}</span>
                      {!enabled && <span className="text-[10px] font-mono text-outline/50 mr-1">soon</span>}
                      <span className="text-xs font-mono text-outline">{creditCost} cr</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block font-mono text-[10px] text-outline uppercase tracking-widest mb-3">
                Alert type (optional)
              </label>
              <div className="flex gap-3">
                {(["email", "webhook"] as const).map((type) => (
                  <button
                    key={type}
                    onClick={() => setAlertType(alertType === type ? "" : type)}
                    disabled={type === "webhook"}
                    className={`flex-1 py-2 rounded-lg border text-sm transition-all ${
                      alertType === type
                        ? "border-secondary/50 bg-secondary/10 text-secondary"
                        : type === "webhook"
                          ? "border-outline-variant/10 text-outline cursor-not-allowed opacity-50"
                          : "border-outline-variant/20 text-on-surface hover:border-outline-variant/40"
                    }`}
                  >
                    {type}{" "}
                    {type === "webhook" && (
                      <span className="text-[8px] text-secondary ml-1">
                        AGENCY
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {alertType && (
              <div>
                <label className="block font-mono text-[10px] text-outline uppercase tracking-widest mb-2">
                  {alertType === "email"
                    ? "Alert email address"
                    : "Webhook URL"}
                </label>
                <input
                  type={alertType === "email" ? "email" : "url"}
                  value={alertDestination}
                  onChange={(e) => setAlertDestination(e.target.value)}
                  className="w-full bg-surface-container-low border-b border-outline focus:border-secondary text-white p-3 outline-none transition-colors"
                  placeholder={
                    alertType === "email"
                      ? "you@company.com"
                      : "https://your-server.com/webhook"
                  }
                />
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setStep(2)}
                className="flex-1 py-3 rounded-lg border border-outline-variant/30 text-white hover:bg-white/5 transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleOnboard}
                disabled={loading || selectedModels.length === 0}
                className="flex-1 py-4 rounded-lg bg-gradient-to-r from-primary to-secondary text-on-primary-container font-bold disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? <LoadingSpinner size="sm" /> : "Start Tracking"}
              </button>
            </div>
            <p className="text-center">
              <button
                onClick={() => navigate("/dashboard")}
                className="text-xs text-outline hover:text-on-surface-variant hover:underline transition-colors"
              >
                Skip for now →
              </button>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
